/**
 * reducer — `(snapshot, NormalizedEvent) → snapshot`. Pure, domain-agnostic.
 *
 * Switches on the canonical `phase` (six values) and delegates everything
 * payload-shaped to the domain's interpreter, which is what keeps ~60 event types
 * from becoming ~60 branches.
 */

import { getDomainInterpreter } from 'src/content/ChatV2/steps/domains/domainRegistry';
import {
  isTerminalPhase,
  phaseRank,
  STEP_PHASE,
  type StepPhase
} from 'src/content/ChatV2/steps/wire/eventType';
import { noteUnknownEvent } from 'src/content/ChatV2/steps/wire/unknownEvents';
import {
  type Step,
  STEP_KIND,
  STEP_STATUS
} from 'src/content/ChatV2/types/steps';

import { resolveStepKey } from './stepKey';
import type {
  DomainInterpreter,
  NormalizedEvent,
  StepKey,
  StepStoreSnapshot
} from './types';

const PHASE_STATUS: Readonly<Record<StepPhase, STEP_STATUS>> = {
  [STEP_PHASE.PENDING]: STEP_STATUS.PENDING,
  [STEP_PHASE.STARTED]: STEP_STATUS.ACTIVE,
  [STEP_PHASE.DELTA]: STEP_STATUS.ACTIVE,
  [STEP_PHASE.BLOCKED]: STEP_STATUS.BLOCKED,
  [STEP_PHASE.COMPLETED]: STEP_STATUS.DONE,
  [STEP_PHASE.FAILED]: STEP_STATUS.FAILED,
  [STEP_PHASE.CANCELLED]: STEP_STATUS.CANCELLED,
  [STEP_PHASE.UNKNOWN]: STEP_STATUS.ACTIVE
};

const TERMINAL_STATUSES: ReadonlySet<STEP_STATUS> = new Set([
  STEP_STATUS.DONE,
  STEP_STATUS.FAILED,
  STEP_STATUS.CANCELLED
]);

export function isTerminalStatus(status: STEP_STATUS): boolean {
  return TERMINAL_STATUSES.has(status);
}

function statusRank(status: STEP_STATUS): number {
  if (status === STEP_STATUS.PENDING) {
    return 0;
  }
  if (isTerminalStatus(status) || status === STEP_STATUS.ORPHANED) {
    return 2;
  }
  return 1;
}

/**
 * Fold one event into the snapshot.
 */
export function applyEvent(
  snapshot: StepStoreSnapshot,
  event: NormalizedEvent
): StepStoreSnapshot {
  const interpreter = getDomainInterpreter(event.domain);

  if (!interpreter) {
    noteUnknownEvent({
      eventType: event.raw.event_type ?? '',
      domain: event.domain,
      phaseRaw: event.phaseRaw
    });
    return snapshot;
  }

  const key = resolveStepKey(event, interpreter);
  const existing = snapshot.byKey.get(key);

  const runIndex = indexRun(snapshot.runIndex, event.runId, key);

  if (!existing) {
    const allowed = interpreter.shouldCreate?.(event) ?? true;
    if (!allowed) {
      return runIndex === snapshot.runIndex
        ? snapshot
        : { ...snapshot, runIndex };
    }
    return insertStep(snapshot, runIndex, key, event, interpreter);
  }

  return updateStep(snapshot, runIndex, key, existing, event, interpreter);
}

function indexRun(
  current: ReadonlyMap<string, StepKey>,
  runId: string | undefined,
  key: StepKey
): ReadonlyMap<string, StepKey> {
  if (!runId || current.get(runId) === key) {
    return current;
  }
  const next = new Map(current);
  next.set(runId, key);
  return next;
}

function insertStep(
  snapshot: StepStoreSnapshot,
  runIndex: ReadonlyMap<string, StepKey>,
  key: StepKey,
  event: NormalizedEvent,
  interpreter: DomainInterpreter
): StepStoreSnapshot {
  const status = PHASE_STATUS[event.phase];
  const lateCreated = isTerminalPhase(event.phase);

  let step: Step = {
    id: key,
    domain: event.domain,
    kind: interpreter.kind?.(event) ?? STEP_KIND.TOOL_SEARCH,
    status,
    runId: event.runId,
    parentKey: event.parentRunId ? runIndex.get(event.parentRunId) : undefined,
    startedAt: event.receivedAt,
    endedAt: isTerminalPhase(event.phase) ? event.receivedAt : undefined,
    ordinal: event.ordinal,
    version: 1,
    ...(lateCreated && { lateCreated: true })
  };

  const label = interpreter.label?.(event);
  if (label) {
    step.label = label;
  }
  step = interpreter.apply?.(step, event) ?? step;
  step.detail = interpreter.detail?.(step);

  const byKey = new Map(snapshot.byKey);
  byKey.set(key, step);

  return {
    order: [...snapshot.order, key],
    byKey,
    runIndex,
    version: snapshot.version + 1
  };
}

function updateStep(
  snapshot: StepStoreSnapshot,
  runIndex: ReadonlyMap<string, StepKey>,
  key: StepKey,
  existing: Step,
  event: NormalizedEvent,
  interpreter: DomainInterpreter
): StepStoreSnapshot {
  const currentRank = statusRank(existing.status);
  const incomingRank = phaseRank(event.phase);
  const blocked =
    isTerminalStatus(existing.status) || incomingRank < currentRank;

  if (blocked) {
    return runIndex === snapshot.runIndex
      ? snapshot
      : { ...snapshot, runIndex };
  }

  let step: Step = {
    ...existing,
    status: PHASE_STATUS[event.phase],
    version: (existing.version ?? 0) + 1
  };
  if (existing.runId == null && event.runId != null) {
    step.runId = event.runId;
  }
  if (existing.parentKey == null && event.parentRunId) {
    step.parentKey = runIndex.get(event.parentRunId);
  }
  if (isTerminalPhase(event.phase)) {
    step.endedAt = event.receivedAt;
  }

  step = interpreter.apply?.(step, event) ?? step;
  step.detail = interpreter.detail?.(step);

  const byKey = new Map(snapshot.byKey);
  byKey.set(key, step);
  return { ...snapshot, byKey, runIndex, version: snapshot.version + 1 };
}

/**
 * Every step still non-terminal becomes `CANCELLED` — the stop-generation counterpart
 * of [orphanNonTerminal](#orphanNonTerminal).
 */
export function cancelNonTerminal(
  snapshot: StepStoreSnapshot
): StepStoreSnapshot {
  let changed = false;
  const byKey = new Map(snapshot.byKey);
  for (const [key, step] of byKey) {
    if (step.status === STEP_STATUS.BLOCKED) {
      continue;
    }
    if (!isTerminalStatus(step.status)) {
      byKey.set(key, {
        ...step,
        status: STEP_STATUS.CANCELLED,
        version: (step.version ?? 0) + 1
      });
      changed = true;
    }
  }
  return changed
    ? { ...snapshot, byKey, version: snapshot.version + 1 }
    : snapshot;
}

/**
 * Every step still non-terminal becomes `ORPHANED`.
 */
export function orphanNonTerminal(
  snapshot: StepStoreSnapshot
): StepStoreSnapshot {
  let changed = false;
  const byKey = new Map(snapshot.byKey);
  for (const [key, step] of byKey) {
    if (step.status === STEP_STATUS.BLOCKED) {
      continue;
    }
    if (
      !isTerminalStatus(step.status) &&
      step.status !== STEP_STATUS.ORPHANED
    ) {
      byKey.set(key, {
        ...step,
        status: STEP_STATUS.ORPHANED,
        version: (step.version ?? 0) + 1
      });
      changed = true;
    }
  }
  return changed
    ? { ...snapshot, byKey, version: snapshot.version + 1 }
    : snapshot;
}
